import { px } from '@/utils/hubScale'
import styled, { css, keyframes } from 'styled-components'

/**
 * Ported from frontend/src/components/design-system/styles.css per #321
 * (FR-019 / FR-021 / FR-022). The salesman list components must NOT import
 * from design-system/, so the relevant slice of the .vc-* global classes is
 * re-expressed here as colocated styled-components.
 */

const COLORS = {
  greenPrimary: '#00b96b',
  greenLight: '#d6f0e6',
  greenDeep: '#047a48',
  greenAccent: '#6dd2ad',
  greenSuccessBg: '#dcfce7',
  greenSuccessFg: '#166534',
  green50: '#ecfdf5',
  green100: '#d1fae5',
  green200: '#a7f3d0',
  green300: '#6ee7b7',
  green500: '#10b981',
  green700: '#047857',
  orangePrimary: '#f97316',
  orangeHover: '#ea580c',
  orange400: '#fb923c',
  orangeLight: '#fff7ed',
  orange50: '#fff7ed',
  orange200: '#fed7aa',
  orangeBorder: '#fed7aa',
  orange600: '#ea580c',
  orange700: '#c2410c',
  amber700: '#b45309',
  blue50: '#eff6ff',
  blue200: '#bfdbfe',
  blue500: '#3b82f6',
  blue700: '#1d4ed8',
  ink50: '#fafaf9',
  ink100: '#f1f1ef',
  ink150: '#eaeae5',
  ink200: '#dcdbd6',
  ink300: '#b8b6ad',
  ink400: '#8a8980',
  ink500: '#65645e',
  ink600: '#4a4945',
  ink700: '#353430',
  ink800: '#222220',
  ink900: '#14130f',
  red50: '#fee2e2',
  red500: '#ef4444',
  red600: '#dc2626',
  amberDeep: '#b45309',
  amberDeepHover: '#92400e',
  redHover: '#b91c1c',
  // Faithful design tokens for the urban-analysis "too late" chip (Project Hub
  // – Improved): a softer red surface than the error-block `red50` above.
  redSurface: '#fef2f2',
  red200: '#fecaca',
  pageBg: '#fbf9f4',
  darkBg: '#548052',
  // Palette from the "Project List (Improved Cards)" design. Deeper and less
  // saturated than the tokens above — the bright orange and green were reading
  // as decoration where these read as status.
  dsGreen: '#2f6b4a',
  dsGreenDeep: '#1f4d34',
  dsRedDeep: '#7a2115',
  dsRed50: '#fdecea',
  dsOrange: '#a2451a',
  dsOrange50: '#fdf1e6',
  dsOrange700: '#8a3c15',
  azDark800: '#222220',
  azDark900: '#14130f',
} as const

export const PageWrapper = styled.div`
  padding: ${px(40)} ${px(56)};
  max-width: ${px(1180)};
  width: 100%;
  margin: 0 auto;
  font-family: 'Geist', 'Inter', system-ui, sans-serif;
  color: ${COLORS.ink900};
`

export const Hello = styled.div`
  font-family: 'Bricolage Grotesque', 'DM Sans', system-ui, sans-serif;
  font-size: ${px(13)};
  font-weight: 500;
  color: ${COLORS.greenPrimary};
  letter-spacing: 0.02em;
  text-transform: uppercase;
  margin-bottom: ${px(8)};
`

export const HelloEmoji = styled.span`
  /* Inline-SVG wave is used here (see HubProjectsList.tsx) instead of
     a Unicode emoji because some Linux distros ship without a color emoji
     font, which renders the wave as a missing-glyph tofu. */
  display: inline-flex;
  align-items: center;
  margin-left: ${px(6)};
  vertical-align: -${px(2)};
  color: ${COLORS.orangePrimary};

  svg {
    width: ${px(16)};
    height: ${px(16)};
    display: block;
  }
`

export const Heading = styled.h1<{ $compact?: boolean }>`
  font-family: 'Bricolage Grotesque', 'DM Sans', system-ui, sans-serif;
  /* $compact is the hub header (Variante B): the title shares its row with the
     lede and the create action, so it stops competing with them for size. */
  font-size: ${(p) => (p.$compact ? px(28) : px(40))};
  line-height: 1.05;
  font-weight: 600;
  letter-spacing: -0.025em;
  color: ${COLORS.ink900};
  margin: ${(p) => (p.$compact ? '0' : `0 0 ${px(24)}`)};
`

export const Eyebrow = styled.div`
  font-size: ${px(12)};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${COLORS.ink500};
  margin-bottom: ${px(8)};
`

// ---------------------------------------------------------------------------
// Compact header — design "Hub — En-tête compact", Variante B
// ("Recherche pleine largeur, barre unique").
//
// What it replaces: a greeting line, a 40px title, a full-width lede, and TWO
// stacked control blocks each carrying its own margin. The design measures that
// at 259px down to the "À faire" heading, and this at 128.
//
// Two rows. The title row carries the heading, the one number that matters, and
// the create action. Under it, a single white bar holds everything else: the
// search takes the free width, and the filters and the sort become controls
// INSIDE it, separated by hairlines rather than by whitespace.
// ---------------------------------------------------------------------------

export const HubHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${px(20)};
  flex-wrap: wrap;
`

/**
 * The lede, cut down to the figure the commercial plans their day around and
 * hung off a rule beside the title. The greeting it used to open with is gone:
 * the sider already names who is signed in, and it cost a line of its own.
 */
export const HeaderLede = styled.p`
  margin: 0;
  padding-left: ${px(20)};
  border-left: ${px(1)} solid ${COLORS.ink200};
  font-size: ${px(14.5)};
  color: ${COLORS.ink500};

  b {
    color: #0f172a;
  }
`

/** The one bar. Wraps rather than overflows on a narrow column. */
export const ControlBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${px(14)};
  flex-wrap: wrap;
  background: white;
  border: ${px(1)} solid ${COLORS.ink200};
  border-radius: ${px(14)};
  padding: ${px(8)} ${px(14)};
  margin: ${px(10)} 0 ${px(14)};
  box-shadow: 0 ${px(1)} ${px(2)} rgba(20, 32, 26, 0.03);
`

/** The search slot — the only thing in the bar that grows. */
export const BarSearch = styled.div`
  flex: 1 1 ${px(220)};
  min-width: ${px(150)};
`

/** The chips, as a group so they wrap together rather than one at a time. */
export const BarFilters = styled.div`
  display: flex;
  align-items: center;
  gap: ${px(8)};
  flex-wrap: wrap;
`

/** The hairline that separates one control from the next inside the bar. */
export const BarDivider = styled.span`
  width: ${px(1)};
  height: ${px(24)};
  flex: none;
  background: ${COLORS.ink200};
`

/** A filter chip — one per action group, and nothing else in the row. */
export const Pill = styled.button<{ $active?: boolean; $compact?: boolean }>`
  /* $compact: inside the header's control bar the chip sits beside a search
     field and a sort, so it takes the smaller of the two sizes the design
     specifies. */
  padding: ${(p) => (p.$compact ? `${px(6)} ${px(13)}` : `${px(9)} ${px(16)}`)};
  border-radius: ${px(999)};
  background: ${(p) => (p.$active ? COLORS.darkBg : 'white')};
  border: ${px(1)} solid ${(p) => (p.$active ? COLORS.darkBg : COLORS.ink300)};
  font-size: ${(p) => (p.$compact ? px(13.5) : px(14))};
  font-weight: 500;
  color: ${(p) => (p.$active ? 'white' : COLORS.ink700)};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${px(8)};
  font-family: inherit;
  transition:
    background 120ms ease,
    border-color 120ms ease;

  &:hover {
    border-color: ${(p) => (p.$active ? COLORS.darkBg : COLORS.ink400)};
  }

  &:focus-visible {
    outline: ${px(2)} solid ${COLORS.orangePrimary};
    outline-offset: ${px(2)};
  }
`

/** The tick that says "selected" without relying on the fill colour alone. */
export const PillTick = styled.span`
  display: inline-grid;
  place-items: center;
  width: ${px(13)};
  height: ${px(13)};
  flex: none;

  svg {
    width: 100%;
    height: 100%;
  }
`

/**
 * "Tout afficher" — a text button, not a chip. Clearing a restriction is not
 * one of the categories, and dressing it as one made the row read as six
 * choices where there are five.
 */
export const PillReset = styled.button`
  align-self: center;
  margin-left: ${px(4)};
  padding: ${px(4)} ${px(6)};
  border: 0;
  background: none;
  font-family: inherit;
  font-size: ${px(13.5)};
  color: ${COLORS.ink500};
  text-decoration: underline;
  text-underline-offset: ${px(3)};
  cursor: pointer;

  &:hover {
    color: ${COLORS.ink900};
  }

  &:focus-visible {
    outline: ${px(2)} solid ${COLORS.orangePrimary};
    outline-offset: ${px(2)};
  }
`

export const PillCount = styled.span<{ $active?: boolean }>`
  background: ${(p) =>
    p.$active ? 'rgba(255, 255, 255, 0.18)' : COLORS.ink100};
  color: ${(p) => (p.$active ? 'white' : COLORS.ink600)};
  font-size: ${px(12)};
  padding: ${px(1)} ${px(7)};
  border-radius: ${px(999)};
  font-weight: 600;
`

export const ProjectsGrid = styled.div<{ $dim?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${px(10)};
  /* Soft dim while a refetch is in flight so the salesman can see that
     the list is updating without a hard reload jump. */
  opacity: ${(p) => (p.$dim ? 0.55 : 1)};
  transition: opacity 200ms ease;
  pointer-events: ${(p) => (p.$dim ? 'none' : 'auto')};
`

const cardFadeIn = keyframes`
  from { opacity: 0; transform: translateY(${px(4)}); }
  to   { opacity: 1; transform: translateY(0); }
`

export const ProjectCardRoot = styled.div`
  background: white;
  border: ${px(1)} solid ${COLORS.ink200};
  border-radius: ${px(16)};
  /* Equal on all four sides. The body below owns every vertical gap, so the
     first line sits as far from the top edge as the last does from the
     bottom. */
  padding: ${px(20)} ${px(22)};
  display: grid;
  grid-template-columns: ${px(72)} minmax(0, 1fr) auto;
  gap: ${px(20)};
  align-items: center;
  box-shadow: 0 ${px(1)} ${px(2)} rgba(20, 32, 26, 0.03);
  cursor: pointer;
  transition:
    border-color 0.15s,
    box-shadow 0.15s,
    transform 0.12s;
  animation: ${cardFadeIn} 220ms ease-out both;

  &:hover {
    border-color: ${COLORS.orangeBorder};
    box-shadow: 0 ${px(6)} ${px(24)} rgba(249, 115, 22, 0.08);
  }

  &:focus-visible {
    outline: ${px(2)} solid ${COLORS.orangePrimary};
    outline-offset: ${px(2)};
  }
`

export type ThumbVariant = 'veranda' | 'pergola' | 'pool' | 'default'

const THUMB_BG: Record<ThumbVariant, string> = {
  veranda: '#d6f0e6',
  pergola: '#fef3c7',
  pool: '#cffafe',
  default: COLORS.ink100,
}
const THUMB_FG: Record<ThumbVariant, string> = {
  veranda: COLORS.greenDeep,
  pergola: '#b45309',
  pool: '#0e7490',
  default: COLORS.ink600,
}

export const Thumbnail = styled.div<{
  $variant: ThumbVariant
  $transparent?: boolean
}>`
  width: ${px(72)};
  height: ${px(72)};
  border-radius: ${px(14)};
  display: grid;
  place-items: center;
  font-size: ${px(26)};
  flex-shrink: 0;
  overflow: hidden;
  background: ${(p) => (p.$transparent ? 'transparent' : THUMB_BG[p.$variant])};
  color: ${(p) => THUMB_FG[p.$variant]};
  border: ${(p) =>
    p.$transparent ? `${px(1)} solid ${COLORS.ink200}` : 'none'};
`

export const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`

// Row holding the client name and, beside it, the small project ref.
export const CardNameRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${px(10)};
  margin-bottom: ${px(2)};
`

export const CardName = styled.div`
  font-family: 'Bricolage Grotesque', 'DM Sans', system-ui, sans-serif;
  font-size: ${px(17)};
  font-weight: 600;
  letter-spacing: -0.02em;
  color: ${COLORS.ink900};
`

export const CardAddress = styled.div`
  font-size: ${px(13)};
  color: ${COLORS.ink500};
  margin-bottom: ${px(6)};
`

/** Company hub only — the owning salesman, appended to the client line rather
 *  than given a row of its own. */
export const CardSalesman = styled.span`
  font-weight: 600;
  color: ${COLORS.ink500};
`

/**
 * The one sentence saying what this dossier wants — "1 document refusé à
 * remplacer", "Rubrique « Terrain » à corriger".
 *
 * It no longer repeats the group name: the section heading above it already
 * says "À corriger", and the card was printing the same two words a second
 * time in a coloured pill. What is left is the part the heading cannot say.
 */
export const StatusDetail = styled.div<{ $tone: SituationTone }>`
  margin-top: ${px(6)};
  font-size: ${px(14)};
  font-weight: 600;
  color: ${(p) =>
    p.$tone === 'critical'
      ? COLORS.dsRedDeep
      : p.$tone === 'action'
        ? COLORS.dsOrange700
        : COLORS.ink500};
`

// Slim 7-position progress track that fills with the project's advancement.
// `margin-left: auto` anchors it to the right edge regardless of whether the
// urban-analysis chip is present.
export const CardProgress = styled.div`
  height: ${px(5)};
  flex: 1 1 auto;
  min-width: ${px(36)};
  max-width: ${px(180)};
  margin-left: auto;
  border-radius: ${px(999)};
  background: ${COLORS.ink100};
  overflow: hidden;
`

export const CardProgressFill = styled.div<{
  $pct: number
  $tone?: 'normal' | 'error'
}>`
  height: 100%;
  width: ${(p) => Math.max(0, Math.min(100, p.$pct))}%;
  border-radius: inherit;
  background: ${(p) =>
    p.$tone === 'error'
      ? COLORS.red500
      : `linear-gradient(90deg, ${COLORS.greenAccent}, ${COLORS.greenPrimary})`};
  transition: width 0.3s ease;
`

export const CardDate = styled.span`
  font-size: ${px(13)};
  color: ${COLORS.ink400};
`

export const CardMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${px(10)};
  text-align: right;
`

/**
 * The right column's information line: how long the dossier has been waiting,
 * where it stands, and its reference.
 *
 * Everything that answers "which one is this and how long has it sat" now
 * lives on this side, which is what stops the card from piling every word on
 * its left half.
 */
export const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: ${px(6)};
  font-size: ${px(12)};
  font-weight: 600;
  color: ${COLORS.ink500};
`

/**
 * How long the commercial has been sitting on this dossier, in three heats.
 *
 * The heat is doubled by the number itself — "23 jours" says it as plainly as
 * the red does — so the badge stays readable to someone who cannot tell the
 * three surfaces apart.
 */
export const WaitBadge = styled.span<{ $heat: 'calm' | 'warm' | 'hot' }>`
  display: inline-flex;
  align-items: center;
  padding: ${px(3)} ${px(9)};
  border-radius: ${px(999)};
  font-size: ${px(12)};
  font-weight: 700;
  white-space: nowrap;
  background: ${(p) =>
    p.$heat === 'hot'
      ? COLORS.dsRed50
      : p.$heat === 'warm'
        ? COLORS.dsOrange50
        : COLORS.ink100};
  color: ${(p) =>
    p.$heat === 'hot'
      ? COLORS.dsRedDeep
      : p.$heat === 'warm'
        ? COLORS.dsOrange700
        : COLORS.ink600};
`

export const CardRef = styled.span`
  font-size: ${px(12)};
  color: ${COLORS.ink400};
`

export const OpenButton = styled.button<{
  $variant?: 'primary' | 'ghost'
  /**
   * Header sizing — smaller than the design's base `.cta`, which was itself
   * smaller than the `.cta.tall` the variant draws. A card's CTA is the only
   * thing on its row and can afford the height; the header's shares a row with
   * a 28px title and a one-line lede, and set that row's height on its own.
   */
  $compact?: boolean
}>`
  display: inline-flex;
  align-items: center;
  gap: ${(p) => (p.$compact ? px(7) : px(10))};
  padding: ${(p) => (p.$compact ? `${px(8)} ${px(16)}` : `${px(14)} ${px(24)}`)};
  font-size: ${(p) => (p.$compact ? px(14) : px(16))};
  font-weight: 600;
  border-radius: ${(p) => (p.$compact ? px(10) : px(13))};
  border: ${(p) =>
    p.$variant === 'primary' ? 'none' : `${px(1.5)} solid ${COLORS.ink200}`};
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  transition:
    transform 0.12s,
    background 0.12s,
    border-color 0.12s;
  background: ${(p) =>
    p.$variant === 'primary' ? COLORS.orangePrimary : 'transparent'};
  color: ${(p) => (p.$variant === 'primary' ? 'white' : COLORS.ink700)};
  /* The glow is scaled with the button: the card CTA's 4/14 spread reads as a
     halo under something a third shorter. */
  box-shadow: ${(p) =>
    p.$variant !== 'primary'
      ? 'none'
      : p.$compact
        ? `0 ${px(2)} ${px(8)} rgba(249, 115, 22, 0.3)`
        : `0 ${px(4)} ${px(14)} rgba(249, 115, 22, 0.35)`};

  &:hover {
    transform: translateY(-${px(1)});
    background: ${(p) =>
      p.$variant === 'primary' ? COLORS.orangeHover : 'white'};
    border-color: ${(p) => (p.$variant === 'primary' ? 'none' : COLORS.ink300)};
  }

  &:focus-visible {
    outline: ${px(2)} solid ${COLORS.orangePrimary};
    outline-offset: ${px(2)};
  }
`

/* ---------- Card CTA · colour keyed on the required action ----------
 * Project Hub – Improved (#xxx / chat16): the card button is colour-coded by
 * the action the salesman must take, instead of every button being the same
 * orange. The tone is derived from `project.step` in HubProjectCard.
 *   send     (orange)      — start / transmit          PROJECT_INITIALIZED
 *   fix      (deep amber)  — rework after refusal      PROJECT_REFUSED
 *   validate (green)       — confirm the project type  PROJECT_ACCEPTED
 *   view     (neutral)     — read-only, architect-side everything else
 *
 * `fix` used to be the error red (#dc2626). Red is reserved for destructive or
 * failed states everywhere else in this app, and a rework CTA is neither — the
 * dossier is fine, it just needs corrections. Worse, "Compléter et envoyer" and
 * "Compléter et renvoyer" differ by one letter, so colour was carrying the whole
 * distinction. The deep amber keeps the two CTAs visibly different without
 * borrowing the danger colour, and the labels now carry the meaning themselves
 * (see SALESMAN_CTA in HubProjectCard).
 */
export type CtaTone = 'send' | 'fix' | 'validate' | 'view'

// Two colours, not three: the design gives "corriger" its burnt orange and
// everything that moves a dossier forward the deep green. Which of the two
// forward actions it is comes from the verb — "Valider le projet" against
// "Compléter et envoyer" — because that is what a reader actually reads.
const CTA_BG: Record<CtaTone, string> = {
  send: COLORS.dsGreen,
  fix: COLORS.dsOrange,
  validate: COLORS.dsGreen,
  view: 'transparent',
}
const CTA_BG_HOVER: Record<CtaTone, string> = {
  send: COLORS.dsGreenDeep,
  fix: COLORS.dsOrange700,
  validate: COLORS.dsGreenDeep,
  view: 'white',
}
const CTA_SHADOW: Record<CtaTone, string> = {
  send: 'none',
  fix: 'none',
  validate: 'none',
  view: 'none',
}

export const CardCta = styled.button<{ $tone: CtaTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${px(8)};
  padding: ${px(12)} ${px(20)};
  font-size: ${px(14.5)};
  font-weight: 700;
  border-radius: ${px(10)};
  border: ${(p) =>
    p.$tone === 'view' ? `${px(1.5)} solid ${COLORS.ink200}` : 'none'};
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  color: ${(p) => (p.$tone === 'view' ? COLORS.ink700 : 'white')};
  background: ${(p) => CTA_BG[p.$tone]};
  box-shadow: ${(p) => CTA_SHADOW[p.$tone]};
  transition:
    transform 0.12s,
    background 0.12s,
    border-color 0.12s;

  &:hover {
    transform: translateY(-${px(1)});
    background: ${(p) => CTA_BG_HOVER[p.$tone]};
    border-color: ${(p) =>
      p.$tone === 'view' ? COLORS.ink300 : 'transparent'};
  }

  &:focus-visible {
    outline: ${px(2)} solid ${COLORS.orangePrimary};
    outline-offset: ${px(2)};
  }
`

/* ---------- Urban-analysis chip · presence & progress ----------
 * Sits between the step kicker and the progress bar. Four tones (chat16):
 *   none-early (orange) — no study, still requestable   step < ACCEPTED
 *   none-late  (red)    — no study, too late to request  step ≥ ACCEPTED
 *   progress   (blue)   — requested, being prepared       status ≠ VALIDATED
 *   delivered  (green)  — PDF available                    status = VALIDATED
 */
export type UaTone = 'none-early' | 'none-late' | 'progress' | 'delivered'

const UA_BG: Record<UaTone, string> = {
  'none-early': COLORS.orange50,
  'none-late': COLORS.redSurface,
  progress: COLORS.blue50,
  delivered: COLORS.green50,
}
const UA_BORDER: Record<UaTone, string> = {
  'none-early': COLORS.orange200,
  'none-late': COLORS.red200,
  progress: COLORS.blue200,
  delivered: COLORS.green200,
}
const UA_FG: Record<UaTone, string> = {
  'none-early': COLORS.orange700,
  'none-late': COLORS.red600,
  progress: COLORS.blue700,
  delivered: COLORS.green700,
}

export const UaChip = styled.span<{ $tone: UaTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${px(7)};
  padding: ${px(4)} ${px(11)} ${px(4)} ${px(9)};
  border-radius: ${px(999)};
  font-size: ${px(11.5)};
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  flex: 0 0 auto;
  background: ${(p) => UA_BG[p.$tone]};
  border: ${px(1)} solid ${(p) => UA_BORDER[p.$tone]};
  color: ${(p) => UA_FG[p.$tone]};

  svg {
    width: ${px(13)};
    height: ${px(13)};
    display: block;
  }
`

export const UaSegs = styled.span`
  display: inline-flex;
  gap: ${px(2)};
`

// Two stacked segments conveying study progress. The first lights up while the
// study is in progress; both fill green once delivered.
export const UaSeg = styled.i<{ $tone: UaTone; $on?: boolean }>`
  width: ${px(8)};
  height: ${px(4)};
  border-radius: ${px(2)};
  display: block;
  background: ${(p) =>
    p.$tone === 'delivered'
      ? COLORS.green700
      : p.$on
        ? COLORS.blue700
        : COLORS.blue200};
`

export const EmptyStateRoot = styled.div`
  background: white;
  border: ${px(1)} dashed ${COLORS.ink300};
  border-radius: ${px(18)};
  padding: ${px(56)} ${px(32)};
  text-align: center;
  margin-top: ${px(16)};
`

export const EmptyTitle = styled.div`
  font-family: 'Bricolage Grotesque', 'DM Sans', system-ui, sans-serif;
  font-size: ${px(22)};
  font-weight: 600;
  color: ${COLORS.ink900};
  margin-bottom: ${px(8)};
`

export const EmptyText = styled.p`
  font-size: ${px(15)};
  color: ${COLORS.ink500};
  margin: 0 0 ${px(24)};
`

export const ErrorBlock = styled.div`
  background: ${COLORS.red50};
  border: ${px(1)} solid #fecaca;
  border-radius: ${px(14)};
  padding: ${px(24)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${px(16)};
  color: ${COLORS.red600};
  margin-top: ${px(16)};
`

/* =====================================================================
 * Phase 2 — Individual project pages (#321 follow-up).
 * The vc-action / vc-step / vc-info / vc-tl / vc-doc / vc-drop / vc-card
 * slices are ported from frontend/src/components/design-system/styles.css
 * and re-expressed here as colocated styled-components. Same reasoning as
 * the phase-1 slice above.
 * ===================================================================== */

export type ActionTone =
  | 'primary'
  | 'wait'
  | 'amber'
  | 'blue'
  | 'success'
  | 'soft'
  | 'danger'

const ACTION_BG: Record<ActionTone, string> = {
  primary: 'white',
  wait: 'white',
  amber: COLORS.orange50,
  blue: COLORS.blue50,
  success: COLORS.green50,
  soft: COLORS.green50,
  danger: COLORS.azDark800,
}
const ACTION_BORDER: Record<ActionTone, string> = {
  primary: COLORS.ink200,
  wait: COLORS.ink200,
  amber: COLORS.orange200,
  blue: COLORS.blue200,
  success: COLORS.green200,
  soft: COLORS.green200,
  danger: COLORS.azDark900,
}
const ACTION_FG: Record<ActionTone, string> = {
  primary: COLORS.ink900,
  wait: COLORS.ink900,
  amber: COLORS.ink900,
  blue: COLORS.ink900,
  success: COLORS.ink900,
  soft: COLORS.ink900,
  danger: 'white',
}

export const ActionBannerRoot = styled.div<{
  $tone: ActionTone
  $vertical?: boolean
}>`
  margin: ${px(28)} 0 ${px(36)};
  background: ${(p) => ACTION_BG[p.$tone]};
  color: ${(p) => ACTION_FG[p.$tone]};
  border: ${px(1)} solid ${(p) => ACTION_BORDER[p.$tone]};
  border-radius: ${px(20)};
  padding: ${px(28)} ${px(32)};
  display: grid;
  grid-template-columns: ${(p) => (p.$vertical ? '1fr' : '1fr auto')};
  gap: ${px(24)};
  align-items: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 ${px(6)} ${px(20)} rgba(20, 19, 15, 0.04);
`

export const ActionEyebrow = styled.div<{ $tone: ActionTone }>`
  font-size: ${px(12.5)};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${(p) =>
    p.$tone === 'amber'
      ? COLORS.orange700
      : p.$tone === 'blue'
        ? COLORS.blue700
        : p.$tone === 'success' || p.$tone === 'soft'
          ? COLORS.green700
          : p.$tone === 'wait'
            ? COLORS.ink500
            : p.$tone === 'danger'
              ? 'white'
              : COLORS.orange600};
  display: flex;
  align-items: center;
  gap: ${px(8)};
  margin-bottom: ${px(8)};
`

export const ActionTitle = styled.div`
  font-family: 'Bricolage Grotesque', 'DM Sans', system-ui, sans-serif;
  font-size: ${px(26)};
  font-weight: 600;
  letter-spacing: -0.02em;
  margin-bottom: ${px(6)};
  line-height: 1.2;
`

export const ActionText = styled.p<{ $tone: ActionTone }>`
  margin: 0;
  color: ${(p) =>
    p.$tone === 'wait' || p.$tone === 'soft'
      ? COLORS.ink500
      : p.$tone === 'amber'
        ? COLORS.amber700
        : p.$tone === 'blue'
          ? COLORS.blue700
          : p.$tone === 'success'
            ? COLORS.green700
            : p.$tone === 'danger'
              ? 'white'
              : COLORS.ink500};
  font-size: ${px(14.5)};
`

export const ActionCtas = styled.div<{ $vertical?: boolean }>`
  display: flex;
  flex-direction: ${(p) => (p.$vertical ? 'row' : 'column')};
  gap: ${px(12)};
  align-items: ${(p) => (p.$vertical ? 'center' : 'stretch')};
`

/* ----- Stepper ----- */

export const StepperRoot = styled.div`
  display: flex;
  background: white;
  border-radius: ${px(16)};
  padding: ${px(8)};
  border: ${px(1)} solid ${COLORS.ink200};
  overflow: hidden;
  margin-bottom: ${px(28)};
`

export type StepStatus = 'done' | 'current' | 'todo'

export const StepRoot = styled.div<{ $status: StepStatus }>`
  flex: 1;
  padding: ${px(16)} ${px(14)};
  border-radius: ${px(12)};
  text-align: center;
  position: relative;
  font-size: ${px(12.5)};
  color: ${COLORS.ink500};
  background: ${(p) =>
    p.$status === 'current'
      ? `linear-gradient(135deg, ${COLORS.green50}, ${COLORS.green100})`
      : 'transparent'};
`

export const StepNum = styled.div<{ $status: StepStatus }>`
  width: ${px(32)};
  height: ${px(32)};
  border-radius: 50%;
  background: ${(p) =>
    p.$status === 'todo' ? COLORS.ink100 : COLORS.green500};
  color: ${(p) => (p.$status === 'todo' ? COLORS.ink500 : 'white')};
  font-weight: 600;
  font-size: ${px(13)};
  display: grid;
  place-items: center;
  margin: 0 auto ${px(8)};
  ${(p) =>
    p.$status === 'current' &&
    css`
      box-shadow: 0 0 0 ${px(4)} rgba(0, 185, 107, 0.25);
    `}
`

export const StepTitle = styled.div<{ $status: StepStatus }>`
  font-weight: ${(p) => (p.$status === 'current' ? 600 : 500)};
  color: ${(p) =>
    p.$status === 'current'
      ? COLORS.green700
      : p.$status === 'done'
        ? COLORS.ink900
        : COLORS.ink700};
  font-size: ${px(12.5)};
  line-height: 1.3;
`

/* ----- InfoList ----- */

export const InfoListRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${px(14)};
`

export const InfoRow = styled.div`
  display: grid;
  grid-template-columns: ${px(100)} 1fr;
  gap: ${px(16)};
  font-size: ${px(14.5)};
  align-items: baseline;
`

export const InfoLabel = styled.span`
  color: ${COLORS.ink500};
`

export const InfoValue = styled.span`
  color: ${COLORS.ink900};
  font-weight: 500;
`

/* ----- Timeline -----
   Moved to `components/projectTimeline.styles.ts`: the rail is no longer a
   uniform green ladder whose only variable is "is this the newest row", so the
   atoms are keyed on the entry's tone instead of on a `$state` flag. */

/* ----- Document row ----- */

export type DocStatus = 'todo' | 'ok' | 'warning'

const DOC_BG: Record<DocStatus, string> = {
  todo: COLORS.ink100,
  ok: COLORS.greenSuccessBg,
  warning: COLORS.orange50,
}
const DOC_FG: Record<DocStatus, string> = {
  todo: COLORS.ink600,
  ok: COLORS.greenSuccessFg,
  warning: COLORS.orange700,
}

export const DocumentRowRoot = styled.div<{ $status: DocStatus }>`
  display: grid;
  grid-template-columns: ${px(44)} 1fr auto auto;
  gap: ${px(16)};
  align-items: center;
  padding: ${px(16)} ${px(20)};
  border-bottom: ${px(1)} solid ${COLORS.ink150};

  &:last-child {
    border-bottom: none;
  }
`

export const DocumentIcon = styled.div<{ $status: DocStatus }>`
  width: ${px(44)};
  height: ${px(44)};
  border-radius: ${px(10)};
  display: grid;
  place-items: center;
  background: ${(p) => DOC_BG[p.$status]};
  color: ${(p) => DOC_FG[p.$status]};
  font-weight: 600;
`

export const DocumentName = styled.div`
  font-size: ${px(15)};
  font-weight: 500;
  color: ${COLORS.ink900};
`

export const DocumentMeta = styled.div`
  font-size: ${px(13)};
  color: ${COLORS.ink500};
  margin-top: ${px(1)};
`

export const DocumentStatusPill = styled.span<{ $status: DocStatus }>`
  display: inline-flex;
  align-items: center;
  gap: ${px(6)};
  padding: ${px(4)} ${px(10)};
  border-radius: ${px(999)};
  background: ${(p) => DOC_BG[p.$status]};
  color: ${(p) => DOC_FG[p.$status]};
  font-size: ${px(12.5)};
  font-weight: 600;
`

export const DocumentSectionHeading = styled.h2`
  font-family: 'Bricolage Grotesque', 'DM Sans', system-ui, sans-serif;
  font-size: ${px(18)};
  font-weight: 600;
  color: ${COLORS.ink900};
  margin: ${px(28)} 0 ${px(8)};
`

/* ----- Drop zone ----- */

export const DropZoneRoot = styled.div`
  border: ${px(2)} dashed ${COLORS.ink300};
  background: ${COLORS.ink50};
  border-radius: ${px(18)};
  padding: ${px(40)};
  text-align: center;
  cursor: default;
  transition: all 0.15s;

  &:hover {
    border-color: ${COLORS.orange400};
    background: ${COLORS.orange50};
  }
`

export const DropZoneIcon = styled.div`
  width: ${px(64)};
  height: ${px(64)};
  border-radius: 50%;
  background: white;
  border: ${px(1)} solid ${COLORS.ink200};
  display: grid;
  place-items: center;
  margin: 0 auto ${px(14)};
  color: ${COLORS.orange600};
  font-size: ${px(28)};
`

export const DropZoneTitle = styled.div`
  font-family: 'Bricolage Grotesque', 'DM Sans', system-ui, sans-serif;
  font-size: ${px(20)};
  font-weight: 600;
  color: ${COLORS.ink900};
  margin-bottom: ${px(6)};
`

export const DropZoneText = styled.div`
  font-size: ${px(14.5)};
  color: ${COLORS.ink500};
  margin-bottom: ${px(16)};
`

/* ----- Cards ----- */

export const CardRoot = styled.div`
  background: white;
  border: ${px(1)} solid ${COLORS.ink200};
  border-radius: ${px(16)};
  padding: ${px(24)};
`

export const CardTitle = styled.div`
  font-family: 'Bricolage Grotesque', 'DM Sans', system-ui, sans-serif;
  font-size: ${px(19)};
  font-weight: 600;
  letter-spacing: -0.015em;
  color: ${COLORS.ink900};
  margin-bottom: ${px(4)};
`

export const CardSub = styled.div`
  font-size: ${px(13.5)};
  color: ${COLORS.ink500};
  margin-bottom: ${px(20)};
`

/* ----- Crumbs ----- */

export const CrumbsRoot = styled.div`
  display: flex;
  align-items: center;
  gap: ${px(8)};
  font-size: ${px(13.5)};
  color: ${COLORS.ink500};
  margin-bottom: ${px(14)};

  a {
    color: ${COLORS.ink500};
    text-decoration: none;
    cursor: pointer;
  }
  a:hover {
    color: ${COLORS.ink700};
  }
`

export const CrumbsCurrent = styled.span`
  color: ${COLORS.ink900};
`

export const CrumbsSeparator = styled.span`
  color: ${COLORS.ink400};
`

/* ----- Detail layout ----- */

export const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: ${px(20)};

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`

export const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${px(16)};
  margin-bottom: ${px(12)};
  flex-wrap: wrap;
`

export const SubHeading = styled.h2`
  font-family: 'Bricolage Grotesque', 'DM Sans', system-ui, sans-serif;
  font-size: ${px(28)};
  line-height: 1.1;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: ${COLORS.ink900};
  margin: 0 0 ${px(16)};
`

export const Lede = styled.p`
  margin: 0 0 ${px(24)};
  font-size: ${px(15.5)};
  color: ${COLORS.ink500};
`

// ---------------------------------------------------------------------------
// Action-oriented list — group headings, situation block, identity row
//
// Colour is load-bearing here, so it is spent sparingly and always doubled by
// a word. `critical` red is the ONLY red on the page and means "someone
// rejected this and is waiting"; `action` orange means "you, now". Anything the
// commercial cannot act on is grey and must not compete. That is why the cards
// no longer paint every actionable dossier orange — when everything is urgent,
// nothing is.
// ---------------------------------------------------------------------------

export type SituationTone = 'critical' | 'action' | 'waiting' | 'settled'

const SITUATION_FG: Record<SituationTone, string> = {
  critical: COLORS.dsRedDeep,
  action: COLORS.dsOrange700,
  waiting: COLORS.ink500,
  settled: COLORS.dsGreenDeep,
}

/**
 * The stack of group sections.
 *
 * The gap lives HERE rather than as a `margin-top` on the heading. It used to
 * be a margin with a `:first-child` exemption — but each heading is the first
 * child of its own `<section>`, so the exemption matched every one of them and
 * the groups ran into each other with no separation at all.
 */
export const GroupSections = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${px(34)};
`

/**
 * Group heading — "À corriger", with its one-line reason for existing.
 *
 * A title now rather than an all-caps kicker: it is the only place the group is
 * named since the cards stopped repeating it, so it has to carry that weight.
 */
export const GroupHeading = styled.div<{ $tone: SituationTone }>`
  font-family: 'Bricolage Grotesque', 'DM Sans', system-ui, sans-serif;
  margin: 0 0 ${px(4)};
  font-size: ${px(18)};
  font-weight: 700;
  letter-spacing: -0.015em;
  color: ${(p) => SITUATION_FG[p.$tone]};
`

export const GroupHint = styled.div`
  font-size: ${px(13.5)};
  color: ${COLORS.ink500};
  margin-bottom: ${px(12)};
`

/**
 * One rhythm for the card's text column.
 *
 * Every row is separated by the same gap and none of them carries a margin of
 * its own — the card used to set those spacings one by one, which is how the
 * bottom edge ended up further from the last line than the top was from the
 * first.
 */
export const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${px(4)};
  min-width: 0;
`

/** The address — promoted to the card's main label. Client names repeat across
 *  a commercial's book (and a single client can hold several dossiers); the
 *  address is what makes a row findable at a glance. */
export const ProjectCardTitle = styled.span`
  font-family: 'Bricolage Grotesque', 'DM Sans', system-ui, sans-serif;
  font-size: ${px(19)};
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${COLORS.ink900};
`

/**
 * The address, trailing the client.
 *
 * The dash is drawn by CSS rather than typed into the text, so it stays out of
 * the accessible name and out of anything copied from the row.
 */
export const CardAddressLine = styled.span`
  font-size: ${px(14)};
  font-weight: 500;
  color: ${COLORS.ink500};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &::before {
    content: '– ';
    color: ${COLORS.ink300};
  }
`

/**
 * Creation date and urban-study state: the two facts that qualify a dossier
 * without asking anything of the reader.
 *
 * Plain text with an icon rather than chips — the card was carrying two boxed
 * pills on one line, and a box is a promise of interaction these do not keep.
 */
export const CardDates = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${px(8)};
  margin-top: ${px(5)};
  font-size: ${px(12.5)};
  font-weight: 600;
  color: ${COLORS.ink500};

  svg {
    width: ${px(12)};
    height: ${px(12)};
    vertical-align: -${px(2)};
    margin-right: ${px(4)};
  }
`

export const MetaSep = styled.span`
  color: ${COLORS.ink300};
`

export const UaText = styled.span<{ $tone: 'warn' | 'done' }>`
  white-space: nowrap;
  color: ${(p) =>
    p.$tone === 'warn' ? COLORS.dsOrange700 : COLORS.dsGreenDeep};
`

export const ProjectCardSubtitle = styled.div`
  font-size: ${px(13)};
  color: ${COLORS.ink500};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/**
 * `#46` — quoted on the phone, so it has to be readable, and read rarely, so
 * it has to be quiet.
 *
 * It sits at the end of the right column's information line. It used to wear a
 * filled badge on the title row, which gave a number nobody scans the same
 * visual weight as the dossier's own name.
 */
export const RefBadge = styled.span`
  flex: none;
  color: ${COLORS.ink400};
  font-weight: 500;
  font-variant-numeric: tabular-nums;
`

/** Client, then address, on one line. */
export const IdentityRow = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: ${px(6)};
  min-width: 0;
`

/** The lifecycle position, in the right column's information line. */
export const CardStep = styled.span`
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`
